/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA;
 */

import config from '@/config';
import { get } from 'svelte/store';
import { log } from '@/core/utils';
import { AuthError } from '@/errors';
import { featuresStore } from '@/stores/features.store';
import { user as currentUserStore } from '@/stores/auth.store';
import { handleRouteError } from './handleRouteError';

/**
 * Guard determines whether a given feature flag is enabled or not
 * @param {string} featureFlag
 * @returns {Promise}
 */
export const getFeaturesGuard = featureFlag => async (next, router) => {
    try {
        await currentUserStore.updateTokenData();
        const user = get(currentUserStore);

        if (!user) {
            log.warn('User is not allowed to access this page');
            await router.redirect({
                backPath: window.location.pathname + window.location.search,
                url: config.routes.login.main
            });
            return;
        }

        const featureStore = get(featuresStore);
        const isEnabled = featureStore.isFeatureEnabled(featureFlag);

        if (!isEnabled) {
            throw new AuthError('User is not allowed to access this page');
        }

        // awaited so that a rejection further down the chain is caught below
        return await next();
    } catch (err) {
        await handleRouteError(err, router);
    }
};
